import { useEffect, useRef } from 'react';
import type { tFocusTrapOptions } from './AlertPanel.types';

/**
 * Elements that can hold focus. `[tabindex="-1"]` is deliberately excluded: it
 * is programmatically focusable but must not be a Tab stop, which is exactly
 * how the panel container itself is marked.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * RET-1957 — a trapped surface can now hide PART of itself while staying
 * mounted: the detail modal hands the screen to a nested checkout and its own
 * chrome goes `visibility: hidden`. Those buttons still match the selector,
 * and `element.focus()` still moves focus to them (verified in Chrome — it is
 * SEQUENTIAL navigation that skips them, not the focus API). That asymmetry is
 * the bug: the cycle's computed "last element" is one the user's Tab will
 * never reach, so the wrap-around never fires and the browser walks focus out
 * of the container onto the page behind. Filtering them out makes the visible
 * set the cycle.
 */
function isVisible(element: HTMLElement): boolean {
  const view = element.ownerDocument.defaultView;
  return !view || view.getComputedStyle(element).visibility !== 'hidden';
}

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      element.getAttribute('aria-hidden') !== 'true' && isVisible(element),
  );
}

/**
 * Keeps Tab inside a surface while it is open, and routes Escape to a
 * dismissal. There is no focus trap anywhere else in this repo, so this is
 * written rather than borrowed.
 *
 * It is mount-scoped on purpose: the caller MOUNTS the trapped surface
 * conditionally rather than hiding it, so "active" and "mounted" are the same
 * thing and there is no hidden-but-alive state to get wrong. Focus moves in on
 * mount; the caller restores focus on unmount, because by then this hook's
 * element is gone.
 */
export function useFocusTrap(options: tFocusTrapOptions): void {
  const { containerRef, onEscape } = options;

  // Held in a REF so the escape handler can change identity without
  // re-running the mount-time focus move. The first version held it in a
  // useCallback keyed on `onEscape`, which re-ran the whole effect — and its
  // focus move — on every parent render that recreated the closure, yanking
  // focus back to the surface's first control mid-interaction (surfaced by
  // RET-1878's detail modal re-rendering the bell under an open panel).
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const [first] = focusableWithin(container);
    // Falls back to the container, which carries tabindex="-1": a panel with
    // nothing focusable in it (the empty state) must still take focus, or Tab
    // continues from wherever the user was and walks straight out of it.
    (first ?? container).focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        // Nothing to move between; keep focus where it is rather than letting
        // the browser hand it to the page behind the panel.
        event.preventDefault();
        return;
      }

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === firstElement || active === container)) {
        event.preventDefault();
        lastElement.focus();
        return;
      }
      if (!event.shiftKey && active === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [containerRef]);
}
